<script setup lang="ts">
import {computed} from "vue";
import type {Component} from "vue";
import {
    Baby,
    Briefcase,
    Calendar,
    ClipboardPlus,
    GraduationCap,
    Handshake,
    HeartHandshake,
    HeartPulse,
    MessageCircle,
    Pill,
    Stethoscope,
    Syringe,
    Tag,
    TestTube
} from "@lucide/vue";

/**
 * The icons the API serves on appointment types and subjects, by their Lucide
 * name. Listed one by one on purpose: `@lucide/vue` ships no dynamic resolver
 * — unlike `lucide-react` and its `DynamicIcon` — so the alternative is a
 * wildcard import that bundles the package's ~1500 icons.
 *
 * Extending the API catalog means adding the name here too.
 */
const CATALOG_ICONS: Record<string, Component> = {
    "baby": Baby,
    "briefcase": Briefcase,
    "calendar": Calendar,
    "clipboard-plus": ClipboardPlus,
    "graduation-cap": GraduationCap,
    "handshake": Handshake,
    "heart-handshake": HeartHandshake,
    "heart-pulse": HeartPulse,
    "message-circle": MessageCircle,
    "pill": Pill,
    "stethoscope": Stethoscope,
    "syringe": Syringe,
    "tag": Tag,
    "test-tube": TestTube
};

const {name, class: className} = defineProps<{
    name?: string | null;
    class?: string;
}>();

/**
 * A missing name draws nothing: the column is nullable and null means "no
 * icon". The calendar stands in for an unknown name only, which happens when
 * an icon chosen from the admin reaches us before the Lucide version that
 * draws it.
 */
const component = computed<Component | null>(() => (name ? CATALOG_ICONS[name] ?? Calendar : null));
</script>

<template>
  <component :is="component" v-if="component" :class="className" />
</template>
